import Loader from '@components/Loader'
import { useSocialPostQuery } from '@graphql/queries/__generated__/social-post.generated'
import { useSocialPostsQuery } from '@graphql/queries/__generated__/social-posts.generated'
import CloseIcon from '@mui/icons-material/Close'
import { Link as BaseLink, Theme, useTheme } from '@mui/material'

import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import useMediaQuery from '@mui/material/useMediaQuery'
import makeStyles from '@mui/styles/makeStyles'
import AvatarImg from 'images/avatar.jpg'
import PlaceHolder from 'images/placeholder-img.svg'
import PlaceHolderImg from 'images/placeholder1080x1080.png'
import React, { useMemo } from 'react'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { SwiperOptions } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { StringParam, useQueryParams } from 'use-query-params'
import { CLIENT_URL } from '../../../constants'
import ChevronLeftIcon from '../../../icons/ChevronLeftIcon'
import { maybe } from '../../utils/misc'
import PostProducts from './PostProducts'
import PostShareButtons from './PostShareButtons'
import { useTranslation } from 'react-i18next'
import { Ordering } from '../../../generated/gqlTypes'

const useStyles = makeStyles((theme: Theme) => ({
  selectedPostImg: {
    '&::before': {
      content: '""',
      backgroundImage: 'inherit',
      filter: 'blur(20px)',
      '-webkit-filter': 'blur(20px)',
      backgroundSize: 'cover',
      backgroundPosition: 'center center',
      backgroundRepeat: 'no-repeat',
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: 0,
    },
  },
  nextPostButton: {
    right: -50,
    top: 'calc(50% - 50px)',
    transform: 'rotate(180deg)',
    [theme.breakpoints.down('sm')]: {
      top: 180,
      right: 0,
    },
  },
  prevPostButton: {
    left: -50,
    top: 'calc(50% - 50px)',

    [theme.breakpoints.down('sm')]: {
      top: 180,
      left: 0,
    },
  },
}))

interface DialogPostProps {}

const DialogPost: React.FC<DialogPostProps> = () => {
  const { t } = useTranslation()
  const classes = useStyles()
  const theme = useTheme()
  const smDown = useMediaQuery(theme.breakpoints.down('sm'))
  const [params, setParams] = useQueryParams({
    postId: StringParam,
  })
  const postQuery = useSocialPostQuery({
    variables: { id: params.postId },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !params.postId,
    onCompleted: data => {
      if (!data?.socialPost) {
        setParams({ postId: undefined }, 'replaceIn')
      }
    },
  })

  const post = useMemo(() => postQuery.data?.socialPost, [postQuery.data])
  const nextPostsQuery = useSocialPostsQuery({
    variables: { first: 1, createdLt: post?.created, orderBy: [{ created: Ordering.Desc }] },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !post,
  })
  const prevPostsQuery = useSocialPostsQuery({
    variables: { last: 1, createdGt: post?.created, orderBy: [{ created: Ordering.Desc }] },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !post,
  })
  const nextPostId = useMemo(
    () => maybe(() => nextPostsQuery.data?.socialPosts.edges[0].node.id, null),
    [nextPostsQuery.data, post]
  )
  const prevPostId = useMemo(
    () => maybe(() => prevPostsQuery.data?.socialPosts.edges[0].node.id, null),
    [prevPostsQuery.data, post]
  )
  const onClose = () => {
    setParams({ postId: undefined })
  }
  const swiperOptions: SwiperOptions = {
    spaceBetween: 0,
    slidesPerView: 1,
    pagination: {
      clickable: true,
      el: `#swiper-pagination-post-images`,
    },
    watchSlidesProgress: true,
    loop: false,
    allowSlideNext: post?.images.length > 1,
    allowSlidePrev: post?.images.length > 1,
  }
  const shareBtnImgUrl = !!post?.images?.length ? `${CLIENT_URL}${post?.images[0].url}` : CLIENT_URL
  return (
    <Dialog
      open={!!params.postId}
      onClose={onClose}
      fullScreen={smDown}
      maxWidth='lg'
      PaperProps={{
        style: {
          overflow: 'visible',
        },
      }}
      keepMounted
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
    >
      <>
        <div className='p-10 md:p-0 flex-grow-0 flex-shrink-0'>
          {post && (
            <div className='md:hidden'>
              <PostShareButtons
                url={window.location.href}
                imgUrl={shareBtnImgUrl}
                title={post?.name}
                description={post?.description || ''}
                hashtag={window.location.pathname}
                iconSize={30}
              />
            </div>
          )}
          <span className='absolute right-10 top-10 z-10'>
            <IconButton onClick={onClose} title={t('close', 'Close')} disableRipple>
              <span className='text-4xl'>
                <CloseIcon fontSize='inherit' />
              </span>
            </IconButton>
          </span>
          {(prevPostsQuery.loading || prevPostId || postQuery.loading) && (
            <span
              className={`absolute z-10 h-100 w-50 flex items-center
							justify-center bg-white opacity-75 hover:opacity-100
							cursor-pointer ${classes.prevPostButton}`}
              onClick={() => (prevPostId ? setParams({ postId: prevPostId }, 'replaceIn') : undefined)}
            >
              <span className='text-5xl'>
                <ChevronLeftIcon viewBox='0 0 512 512' fontSize='inherit' />
              </span>
            </span>
          )}
          {(nextPostsQuery.loading || nextPostId || postQuery.loading) && (
            <span
              className={`absolute z-10 h-100 w-50 flex items-center
							justify-center bg-white opacity-75 hover:opacity-100
							cursor-pointer ${classes.nextPostButton}`}
              onClick={() => (nextPostId ? setParams({ postId: nextPostId }, 'replaceIn') : undefined)}
            >
              <span className='text-5xl'>
                <ChevronLeftIcon viewBox='0 0 512 512' fontSize='inherit' />
              </span>
            </span>
          )}
        </div>
        <DialogContent className='max-w-full relative bg-gray-50 md:bg-white' style={{ width: 1000, padding: 0 }}>
          <Loader
            className='spinner'
            type='ThreeDots'
            color='#333'
            visible={postQuery.loading && !post}
            height={100}
            width={100}
          />
          <div className='flex flex-wrap'>
            <div className='w-full md:w-3/5' style={{ borderRight: '1px solid #efefef' }}>
              <div className='p-0 md:p-20 min-h-400 md:min-h-600 overflow-hidden relative'>
                {post?.username && (
                  <span className='md:hidden absolute top-10 left-10 z-10'>
                    <Chip
                      color='primary'
                      label={
                        <BaseLink
                          href={post.externalLink}
                          target='_blank'
                          title={t('openANewWindow', 'Open a new window')}
                        >
                          <span className='font-bold text-base text-white'>@{post.username || ''}</span>
                        </BaseLink>
                      }
                    />
                  </span>
                )}
                {!postQuery.loading && !post?.images.length && (
                  <div
                    className={`relative w-full h-full overflow-hidden ${classes.selectedPostImg}`}
                    style={{
                      backgroundImage: `url(${PlaceHolderImg})`,
                    }}
                  >
                    <LazyLoadImage
                      alt={post?.name}
                      effect='opacity'
                      placeholderSrc={PlaceHolder}
                      src={PlaceHolderImg}
                      wrapperClassName='w-full h-full absolute'
                      className='w-full h-full object-contain object-center'
                    />
                  </div>
                )}
                {post?.images.length > 0 && (
                  <>
                    <Swiper {...swiperOptions}>
                      {post.images.map(node => {
                        return (
                          <SwiperSlide key={node.id}>
                            <div
                              className={`relative h-360 md:h-560 w-full ${classes.selectedPostImg}`}
                              style={{
                                backgroundImage: `url(${node.url})`,
                              }}
                            >
                              <LazyLoadImage
                                alt={post?.name}
                                effect='opacity'
                                placeholderSrc={PlaceHolder}
                                src={node.url}
                                wrapperClassName='w-full h-full absolute'
                                className='w-full h-full object-contain object-center'
                              />
                            </div>
                          </SwiperSlide>
                        )
                      })}
                    </Swiper>
                    <div
                      id={`swiper-pagination-post-images`}
                      className={`swiper-pagination swiper-pagination-clickable swiper-pagination-bullets w-full ${
                        post.images.length > 1 ? 'block' : 'hidden'
                      }`}
                    />
                  </>
                )}
              </div>
              <div className='p-20 bg-gray-50 h-100 hidden md:block'>
                <div className='flex items-center'>
                  <img className='mr-15' src={AvatarImg} alt='@dmc_france' width={40} height={40} />
                  <div className='flex-1'>
                    <BaseLink
                      href={post?.externalLink}
                      target='_blank'
                      title={t('openANewWindow', 'Open a new window')}
                    >
                      <span className='font-bold text-base'>@{post?.username || ''}</span>
                    </BaseLink>
                  </div>
                  <div>
                    {post && (
                      <PostShareButtons
                        url={window.location.href}
                        imgUrl={shareBtnImgUrl}
                        title={post?.name}
                        description={post?.description || ''}
                        hashtag={window.location.pathname}
                        iconSize={22}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className='w-full md:w-2/5 flex flex-col'>
              <div className='py-20 md:px-40'>
                <PostProducts products={post?.relatedProducts || []} />
              </div>
              <div style={{ borderTop: '1px solid #efefef' }} />
              <div className='flex-1 relative overflow-x-hidden my-10 min-h-200'>
                <div className='absolute inset-0 overflow-y-scroll px-20 md:px-40' style={{ right: -10 }}>
                  <pre>{post?.description}</pre>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </>
    </Dialog>
  )
}

export default DialogPost
